/**
 * StaffIDVerify
 * =============
 * Public, no-auth landing page at /verify/staff-id/:token.
 *
 * This is what a receiving school sees when they scan the QR code on a
 * Kids WiseApp staff member's ID card. It does a server-side lookup
 * and renders one of four states:
 *
 *   • valid       — green tick + the same badge they're holding up
 *   • revoked     — red "no longer valid" with a clear safeguarding note
 *   • expired     — amber "this card has expired" + ask staff to refresh
 *   • not_found   — generic "this link is invalid"
 *
 * No PII besides name + role + org + (optional) avatar is exposed — the
 * backend strips everything else on this endpoint.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../utils/api';

const BRAND_GRADIENT = 'linear-gradient(135deg, #00abc9 0%, #2abb9b 100%)';

export default function StaffIDVerify() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    api.verifyStaffId(token)
      .then(d => { if (!cancelled) setState({ loading: false, data: d }); })
      .catch(() => { if (!cancelled) setState({ loading: false, data: { unreachable: true } }); });
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <Shell>
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#4b6b6f' }}>
          Verifying ID card…
        </div>
      </Shell>
    );
  }

  const d = state.data || {};
  if (d.unreachable) {
    return (
      <Shell>
        <Banner kind="error" title="Couldn't verify right now">
          We couldn't reach Kids WiseApp to verify this card. Please check
          the staff member's photo ID and call the listed setting instead.
        </Banner>
      </Shell>
    );
  }
  if (d.not_found) {
    return (
      <Shell>
        <Banner kind="error" title="ID card not recognised">
          We couldn't find a staff ID card matching this QR code. Please
          ask the staff member to show you their photo ID instead.
        </Banner>
      </Shell>
    );
  }
  if (d.status === 'revoked' || d.status === 'expired') {
    const isRevoked = d.status === 'revoked';
    return (
      <Shell>
        <Banner kind={isRevoked ? 'error' : 'warn'} title={isRevoked ? 'This ID card has been revoked' : 'This ID card has expired'}>
          {isRevoked
            ? 'This person is no longer authorised as a staff member at this setting. Do not release a child to them — contact the setting directly.'
            : "This card hasn't been renewed. Please ask the staff member to refresh their card in the app, and verify their identity in person before proceeding."}
        </Banner>
        <ReadOnlyBadge card={d} mutedReason={d.status} />
      </Shell>
    );
  }

  // Valid
  return (
    <Shell>
      <Banner kind="ok" title="ID card verified">
        This person is a currently authorised staff member at the setting below.
      </Banner>
      <ReadOnlyBadge card={d} />
      <div style={{
        marginTop: 18,
        fontSize: 12, color: '#4b6b6f',
        textAlign: 'center',
      }} data-testid="verify-timestamp">
        Verified live at {new Date(d.verified_at || Date.now()).toLocaleString('en-GB')}.
      </div>
    </Shell>
  );
}

// ---- Sub-components ----

const Shell = ({ children }) => (
  <div data-testid="verify-staff-id-page" style={{
    minHeight: '100vh',
    background: '#f4fbfc',
    padding: '40px 16px',
    fontFamily: "'Figtree', system-ui, sans-serif",
    color: '#0a1f24',
  }}>
    <div style={{ maxWidth: 460, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, justifyContent: 'center' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: BRAND_GRADIENT,
          color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13,
        }}>KW</div>
        <span style={{ fontWeight: 800, fontSize: 18 }}>Kids WiseApp</span>
      </div>
      {children}
    </div>
  </div>
);

const Banner = ({ kind, title, children }) => {
  const map = {
    ok:    { bg: '#dbf5ef', fg: '#0b6b58', icon: '✓' },
    warn:  { bg: '#fff3d6', fg: '#7a5a13', icon: '!' },
    error: { bg: '#fde4e4', fg: '#9b2c2c', icon: '✕' },
  };
  const c = map[kind] || map.ok;
  return (
    <div
      data-testid={`verify-banner-${kind}`}
      style={{
        background: c.bg, color: c.fg,
        borderRadius: 16, padding: '14px 16px',
        marginBottom: 16,
        display: 'flex', gap: 12, alignItems: 'flex-start',
      }}
    >
      <span style={{
        width: 28, height: 28, borderRadius: '50%',
        background: c.fg, color: c.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, flexShrink: 0,
      }}>{c.icon}</span>
      <div>
        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
};

const ReadOnlyBadge = ({ card, mutedReason }) => {
  const dim = !!mutedReason;
  return (
    <div data-testid="verify-staff-id-badge" style={{
      background: '#fff', borderRadius: 18, overflow: 'hidden',
      border: '1px solid rgba(10,31,36,0.08)',
      boxShadow: '0 12px 30px rgba(6,34,42,0.10)',
      opacity: dim ? 0.7 : 1,
    }}>
      <div style={{
        padding: '14px 16px',
        background: card.organization?.primary_color
          ? `linear-gradient(135deg, ${card.organization.primary_color} 0%, #2abb9b 100%)`
          : BRAND_GRADIENT,
        display: 'flex', gap: 12, alignItems: 'center',
      }}>
        {card.organization?.logo_url ? (
          <img src={card.organization.logo_url} alt="" style={{ height: 32, borderRadius: 6, background: '#fff', padding: 4 }} />
        ) : (
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'rgba(255,255,255,0.92)', color: '#00abc9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800,
          }}>{(card.organization?.name || 'KW').slice(0, 2).toUpperCase()}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{card.organization?.name || 'Setting'}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.92)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>
            Staff Identification
          </div>
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14 }}>
          {card.avatar_url ? (
            <img src={card.avatar_url} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: '#cdeff5', color: '#00abc9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, fontWeight: 800,
            }}>{(card.full_name || '?').charAt(0).toUpperCase()}</div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div data-testid="verify-staff-id-name" style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1, marginBottom: 4 }}>
              {card.full_name}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#4b6b6f' }}>
              {card.role_label}{card.assigned_room_name ? ` · ${card.assigned_room_name}` : ''}
            </div>
          </div>
        </div>
        <div style={{
          background: '#f4fbfc', borderRadius: 12, padding: '10px 12px',
          marginBottom: 12,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#4b6b6f', letterSpacing: '0.14em' }}>STAFF ID</div>
          <div data-testid="verify-staff-id-code" style={{
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: 20, fontWeight: 800, marginTop: 2,
          }}>{card.staff_id_code || '—'}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#4b6b6f',
                      borderTop: '1px dashed rgba(10,31,36,0.10)', paddingTop: 10 }}>
          <span>Issued: {fmtDate(card.issued_at)}</span>
          <span>Expires: {fmtDate(card.expires_at)}</span>
        </div>
      </div>
    </div>
  );
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
